/**
 * WebSocket 실시간 연결 Hook
 *
 * 플레이리스트 생성, 트랙 재생성, 비용 대시보드 모니터링을 위한 WebSocket 연결을 관리합니다.
 * 자동 재연결, 메시지 타입별 핸들링, snake_case → camelCase 변환을 지원합니다.
 *
 * @example
 * const { status, connect, on, off } = useWebSocket();
 *
 * useEffect(() => {
 *   connect(playlistId, 'playlist');
 *   on('progress_update', handleProgress);
 *   return () => { off('progress_update', handleProgress); };
 * }, [playlistId, connect, on, off]);
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { getAccessToken } from '@/lib/auth/cookies';
import { logger } from '@/lib/utils/logger';

import type {
  WebSocketMessage,
  WebSocketStatus,
  UseWebSocketOptions,
  UseWebSocketReturn,
} from './types';

// ============================================================================
// Utility: snake_case → camelCase 변환
// ============================================================================

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

function convertSnakeToCamelInternal(obj: JsonValue): JsonValue {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(convertSnakeToCamelInternal);
  }

  if (typeof obj !== 'object') {
    return obj;
  }

  const newObj: { [key: string]: JsonValue } = {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const camelKey = key.replace(/_([a-z])/g, (_, letter: string) =>
        letter.toUpperCase()
      );
      newObj[camelKey] = convertSnakeToCamelInternal(obj[key]);
    }
  }

  return newObj;
}

function convertSnakeToCamel<T>(obj: unknown): T {
  return convertSnakeToCamelInternal(obj as JsonValue) as T;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/** 연결 타임아웃 (30초) */
const CONNECTION_TIMEOUT_MS = 30000;

export function useWebSocket(
  options: UseWebSocketOptions = {}
): UseWebSocketReturn {
  const {
    reconnectAttempts = 5,
    reconnectDelay = 3000,
  } = options;

  // State
  const [status, setStatus] = useState<WebSocketStatus>('disconnected');
  const [error, setError] = useState<string>();

  // Refs
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectCountRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const listenersRef = useRef<Map<string, Set<(msg: WebSocketMessage) => void>>>(
    new Map()
  );

  // ============================================================================
  // Internal Functions
  // ============================================================================

  const getWebSocketUrl = useCallback(
    (resourceId: string, type: 'playlist' | 'track' | 'cost-dashboard'): string => {
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';

      let host = window.location.host;
      const hostname = window.location.hostname;

      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
        try {
          const apiPort = new URL(apiUrl).port || '8000';
          host = `${hostname}:${apiPort}`;
        } catch {
          host = `${hostname}:8000`;
        }
      }

      const token = getAccessToken();
      const tokenQuery = token ? `?token=${encodeURIComponent(token)}` : '';

      if (type === 'playlist') {
        return `${protocol}://${host}/ws/playlist/${resourceId}/${tokenQuery}`;
      } else if (type === 'track') {
        return `${protocol}://${host}/ws/track/${resourceId}/${tokenQuery}`;
      } else {
        return `${protocol}://${host}/ws/cost-dashboard/${tokenQuery}`;
      }
    },
    []
  );

  const emitEvent = useCallback((eventType: string, message: WebSocketMessage) => {
    const listeners = listenersRef.current.get(eventType);
    if (listeners) {
      listeners.forEach((callback) => callback(message));
    }
  }, []);

  const closeConnection = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setStatus('disconnected');
  }, []);

  const cleanup = useCallback(() => {
    closeConnection();
    listenersRef.current.forEach((listeners) => {
      listeners.clear();
    });
    listenersRef.current.clear();
  }, [closeConnection]);

  // ============================================================================
  // Public API
  // ============================================================================

  const connect = useCallback(
    (resourceId: string, type: 'playlist' | 'track' | 'cost-dashboard') => {
      if (wsRef.current) {
        closeConnection();
      }

      setStatus('connecting');

      try {
        const url = getWebSocketUrl(resourceId, type);
        const ws = new WebSocket(url);

        ws.onopen = () => {
          if (connectionTimeoutRef.current) {
            clearTimeout(connectionTimeoutRef.current);
            connectionTimeoutRef.current = null;
          }
          setStatus('connected');
          reconnectCountRef.current = 0;
        };

        ws.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            const convertedMessage = convertSnakeToCamel<WebSocketMessage>(message);
            emitEvent(convertedMessage.type, convertedMessage);
          } catch (error) {
            logger.error('Failed to parse WebSocket message:', error);
          }
        };

        ws.onerror = (event) => {
          logger.error('WebSocket error:', event);
          setStatus('error');

          let errorMessage = '알 수 없는 WebSocket 에러';
          const readyState = ws.readyState;
          switch (readyState) {
            case WebSocket.CONNECTING:
              errorMessage = '연결 중...';
              break;
            case WebSocket.OPEN:
              errorMessage = '연결됨 (에러 이벤트)';
              break;
            case WebSocket.CLOSING:
              errorMessage = '연결 종료 중...';
              break;
            case WebSocket.CLOSED:
              errorMessage = '연결이 닫혔습니다. 백엔드 서버가 응답하지 않거나 WebSocket 경로가 잘못되었을 수 있습니다.';
              break;
          }

          setError(errorMessage);
        };

        ws.onclose = (event: CloseEvent) => {
          setStatus('disconnected');

          const code = event.code ?? 1000;
          const wasClean = event.wasClean === true;

          let closeMessage = '';
          if (code === 1006) {
            closeMessage = '백엔드 서버와의 연결이 끊어졌습니다. 서버가 실행 중인지 확인해주세요.';
          } else if (code === 1000) {
            closeMessage = '정상적으로 연결이 종료되었습니다.';
          } else if (code === 1002) {
            closeMessage = '프로토콜 에러가 발생했습니다. WebSocket 경로를 확인해주세요.';
          } else if (code === 1008) {
            closeMessage = '정책 위반으로 인해 연결이 종료되었습��다.';
          } else {
            closeMessage = `연결이 종료되었습니다. (코드: ${code})`;
          }

          if (!wasClean) {
            setError(closeMessage);
          }

          if (
            reconnectCountRef.current < reconnectAttempts &&
            wsRef.current !== null
          ) {
            reconnectCountRef.current++;
            reconnectTimeoutRef.current = setTimeout(() => {
              connect(resourceId, type);
            }, reconnectDelay * Math.pow(2, reconnectCountRef.current - 1));
          }
        };

        wsRef.current = ws;

        connectionTimeoutRef.current = setTimeout(() => {
          if (ws.readyState !== WebSocket.OPEN) {
            logger.error('WebSocket connection timeout');
            setStatus('error');
            ws.close();
            wsRef.current = null;
          }
        }, CONNECTION_TIMEOUT_MS);
      } catch (error) {
        logger.error('Failed to create WebSocket:', error);
        setStatus('error');
      }
    },
    [getWebSocketUrl, emitEvent, reconnectAttempts, reconnectDelay, closeConnection]
  );

  const disconnect = useCallback(() => {
    reconnectCountRef.current = reconnectAttempts;
    closeConnection();
  }, [reconnectAttempts, closeConnection]);

  const on = useCallback(
    (eventType: string, callback: (message: WebSocketMessage) => void) => {
      if (!listenersRef.current.has(eventType)) {
        listenersRef.current.set(eventType, new Set());
      }
      listenersRef.current.get(eventType)!.add(callback);
    },
    []
  );

  const off = useCallback(
    (eventType: string, callback: (message: WebSocketMessage) => void) => {
      const listeners = listenersRef.current.get(eventType);
      if (listeners) {
        listeners.delete(callback);
      }
    },
    []
  );

  const send = useCallback((data: Record<string, unknown>): boolean => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      logger.error('WebSocket이 연결되지 않았습니다. 현재 상태:', wsRef.current?.readyState);
      return false;
    }

    try {
      wsRef.current.send(JSON.stringify(data));
      return true;
    } catch (error) {
      logger.error('WebSocket 메시지 전송 실패:', error);
      return false;
    }
  }, []);

  const sendWhenReady = useCallback(async (
    data: Record<string, unknown>,
    maxWaitMs = 5000
  ): Promise<boolean> => {
    const startTime = Date.now();
    const checkInterval = 100;

    while (Date.now() - startTime < maxWaitMs) {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        return send(data);
      }
      await new Promise(resolve => setTimeout(resolve, checkInterval));
    }

    logger.error('WebSocket 연결 대기 시간 초과');
    return false;
  }, [send]);

  // ============================================================================
  // Cleanup
  // ============================================================================

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    status,
    isConnected: status === 'connected',
    error,
    connect,
    disconnect,
    on,
    off,
    send,
    sendWhenReady,
    ws: wsRef.current,
  };
}
